import { Button } from './Button.js';
import { cn } from './cn.js';
import { Select } from './Select.js';

/** O que o seletor oferece quando quem chama não diz outra coisa. */
const PER_PAGE_OPTIONS = [10, 25, 50, 100];

/**
 * O paginador da interna — o primeiro do produto.
 *
 * ## Por que ele não existia, e por que passou a existir
 *
 * As listas da casa pediam uma página só (`page: 1, perPage: 50`) e mostravam o
 * aviso de corte de `lib/list.ts`: *"Mostrando 50 de 120. Use a busca e os
 * filtros para chegar ao resto."* Isso funciona numa tela cheia, onde os
 * filtros estão ao lado da lista e a rolagem é da página — e seis telas seguem
 * assim.
 *
 * Num modal não funciona: não há filtro para estreitar, e as linhas são a
 * resposta inteira que ele foi aberto para dar. As 37 placas de uma competência
 * faziam o diálogo medir 1817px numa janela de 800 — ver o docblock de `Modal`
 * em `packages/ui`, onde a altura foi consertada. Este componente é a outra
 * metade: a lista deixa de vir inteira.
 *
 * ## As páginas numeradas, desde 28/09/2026 (referência 07)
 *
 * Até ali eram só "Anterior", "Próxima" e um "2 / 4" no meio. O desenho novo
 * põe os números: a primeira, a última, a atual e as vizinhas dela, com "…"
 * onde há páginas escondidas — ver `pageItems`. A página atual é contornada no
 * laranja da casa e leva `aria-current="page"`. As setas são só ícone, com o
 * nome dito por extenso para o leitor de tela.
 *
 * O tamanho é o da régua da casa, e não os 52px da referência: o controle tem
 * 36px no mouse e 44px no dedo (`DESIGN.md`), e o paginador divide a linha com
 * o seletor, que é um `Select` da mesma altura.
 *
 * A frase conta LINHAS e não páginas ("11 a 20 de 37"), porque é o mesmo número
 * que o resumo acima do modal mostra — e o modal existe para conferir números
 * um contra o outro.
 *
 * ## O seletor de quantidade, e a ratoeira que ele quase criou
 *
 * `onPerPageChange` é **opcional**: sem ele o componente é o de antes, e é
 * assim que o modal do ranking continua com as suas dez linhas fixas. Com ele,
 * a tela ganha o controle de quantas linhas cabem numa página — o primeiro do
 * produto.
 *
 * O que decide se cada metade aparece **não é a mesma pergunta**, e tratá-las
 * como uma só produz o defeito que esta versão evita: com a regra antiga
 * ("uma página só, devolve `null`"), escolher 100 numa lista de 40 linhas
 * faria o paginador inteiro sumir — junto com o seletor —, e **não haveria
 * como voltar para 10**. A tela teria engolido o próprio controle.
 *
 * Então:
 *
 *   · os **botões** aparecem quando há mais de uma página, que é quando virar
 *     página quer dizer alguma coisa;
 *   · o **seletor** aparece quando a menor opção dividiria a lista — abaixo
 *     disso toda escolha mostra as mesmas linhas, e um controle que não muda
 *     nada é ruído que ensina a pessoa a não olhar para ali.
 *
 * Com as duas metades escondidas, o componente continua devolvendo `null`.
 *
 * A frase de linhas acompanha os BOTÕES, e não o seletor: ela responde "onde
 * estou", e numa página só não há onde estar — ali ela viraria o total repetido
 * logo abaixo do título do cartão, que já o diz.
 *
 * ## O "Todos", que entrou em 15/09 e saiu em 13/10/2026
 *
 * O seletor oferecia 10, 25, 50, 100 e Todos. A quinta opção foi retirada a
 * pedido do responsável: as opções são as quatro, sempre, em toda lista, com
 * dez por padrão.
 */
/**
 * Os botões de página a desenhar: a primeira, a última, a atual e as vizinhas
 * dela, com `'…'` no lugar de cada trecho escondido.
 *
 * Reticências no lugar de UMA página só esconderiam um número para pôr outro
 * símbolo do mesmo tamanho — ali a página aparece. Por isso "1 2 3 4 5" nunca
 * vira "1 … 3 4 5".
 */
export function pageItems(page: number, pages: number): Array<number | '…'> {
  const shown = new Set([1, pages, page - 1, page, page + 1]);
  const items: Array<number | '…'> = [];
  for (let n = 1; n <= pages; n++) {
    if (shown.has(n)) {
      items.push(n);
    } else if (shown.has(n - 1) && shown.has(n + 1)) {
      items.push(n);
    } else if (items.at(-1) !== '…') {
      items.push('…');
    }
  }
  return items;
}

/*
 * O quadrado das SETAS — a altura do controle da casa (36px no mouse, 44px no
 * dedo) nas duas medidas, sem o respiro lateral do `Button`.
 */
// @valor
const SQUARE = 'w-9 px-0 pointer-coarse:w-11';

/*
 * O botão de NÚMERO cresce com o número, a partir do quadrado.
 *
 * Era o mesmo quadrado das setas, e o cadastro de veículos da CE tem 54.692
 * páginas de dez: cinco dígitos espremidos num botão de 36px, colados na
 * borda (medido em 19/10/2026). `min-w` guarda o quadrado para os números
 * curtos, e o respiro lateral dá ar aos longos.
 */
// @valor
const NUMBER = 'min-w-9 px-2 pointer-coarse:min-w-11';

/*
 * A página ATUAL: o laranja da casa por inteiro — borda, texto e um fundo
 * suave. Só a borda se perdia no tema escuro, onde o contorno laranja fino
 * fica parecido com o contorno neutro dos outros botões.
 */
// @valor
const CURRENT = 'bg-primaria-suave font-semibold';

/** O número como se lê em português: `54.692`, e não `54692`. */
export function pageNumber(n: number): string {
  return n.toLocaleString('pt-BR');
}

export function Pagination({
  page: asked,
  perPage,
  total,
  onChange,
  label,
  onPerPageChange,
  perPageOptions = PER_PAGE_OPTIONS,
  className,
}: {
  page: number;
  perPage: number;
  /** O total de LINHAS, não de páginas — é o que o servidor devolve. */
  total: number;
  onChange: (page: number) => void;
  /**
   * O que está sendo paginado, no plural: "vendas", "usuários".
   *
   * Entra nos nomes acessíveis dos dois botões e do seletor. "Próxima" sozinho,
   * repetido em duas listas da mesma tela, deixa quem navega por leitor de tela
   * com dois botões de mesmo nome e destinos diferentes.
   */
  label: string;
  /**
   * Quando existe, a tela deixa a pessoa escolher quantas linhas cabem numa
   * página. Quem passa isto guarda o valor no próprio estado e o manda de volta
   * em `perPage` — o componente não lembra de nada.
   *
   * **Trocar a quantidade tem que voltar para a página 1**, e quem faz isso é
   * quem chama: estar na página 4 de 10 linhas e passar para 100 por página
   * pediria ao servidor um `offset` que já passou do fim, e a resposta seria uma
   * lista vazia sobre um total que diz haver 37 linhas.
   */
  onPerPageChange?: (perPage: number) => void;
  /**
   * As opções do seletor. **Nenhuma pode passar de 200**: é o teto de `perPage`
   * em `paginationQuery` (`packages/contract/src/common.ts`), e um valor acima
   * dele volta 400 da API sem que a tela tenha como explicar o motivo.
   */
  perPageOptions?: number[];
  /**
   * O espaçamento em volta, quando quem monta precisa dele.
   *
   * Existe por causa do `Card` com `bleed`, onde a tabela encosta nas bordas e
   * quem desenha o próprio padding é cada peça. Embrulhar o paginador numa
   * `<div>` com padding do lado de fora resolveria o espaço e criaria outro
   * defeito: quando não há o que paginar o componente devolve `null`, e a
   * `<div>` de fora continuaria ali — uma faixa vazia com borda, sob toda lista
   * curta. Vindo por aqui, o padding some junto com o que ele envolvia.
   */
  className?: string;
}) {
  const pages = Math.max(1, Math.ceil(total / perPage));
  /*
   * A página pedida, limitada ao que existe. Desde 19/10/2026 a página vem da
   * URL, e um link antigo (ou a lista que encolheu depois de um filtro) pede a
   * 27.346 de 7.749: sem o limite, o paginador ficava sem página atual e com a
   * seta de avançar desligada. A lista leva a URL para a última página
   * (`useClampedPage`); aqui, o desenho já mostra onde ela vai cair.
   */
  const page = Math.min(Math.max(1, asked), pages);

  const showButtons = pages > 1;
  /*
   * `Math.min` de uma lista vazia é `Infinity`, e `total > Infinity` é falso —
   * então `perPageOptions={[]}` esconde o seletor em vez de desenhar uma caixa
   * sem opção nenhuma. É o comportamento certo, e não um acidente feliz: uma
   * lista vazia é quem chama dizendo que não quer o controle.
   */
  const showPicker = onPerPageChange !== undefined && total > Math.min(...perPageOptions);

  if (!showButtons && !showPicker) return null;

  /*
   * As quatro opções aparecem SEMPRE, e não só as que repartem a lista.
   *
   * A primeira versão desta leva escondia as maiores que o total — "oferecer
   * 100 numa lista de 35 é oferecer a mesma tela com outro nome". O argumento
   * é bom e o efeito é ruim: quem aprendeu onde fica o "25" encontra a caixa
   * com um conteúdo diferente a cada recorte, e um controle que muda de forma
   * conforme o dado é um controle em que não se confia.
   *
   * Foi medido: `internal.sales.spec.ts` escolhe 25 numa competência de doze
   * vendas, e a opção não existia mais. São 10, 25, 50 e 100, sempre.
   *
   * O valor em uso entra mesmo quando não é nenhuma das quatro, porque um
   * `<select>` cujo `value` não casa com nenhuma `<option>` não reclama: ele
   * desenha a primeira e segue. A caixa diria "10 por página" sobre uma lista
   * de cinquenta, e o único jeito de descobrir seria contar as linhas.
   */
  const options = [...new Set([...perPageOptions, perPage])].sort((a, b) => a - b);

  /*
   * O fim é limitado pelo total: na última página a conta `page * perPage`
   * passaria do fim e a frase anunciaria linhas que não existem — "31 a 40 de
   * 37", que é a tela afirmando o que ela mesma acabou de negar.
   */
  const from = (page - 1) * perPage + 1;
  const until = Math.min(page * perPage, total);

  return (
    <div className={cn('flex flex-wrap items-center justify-between gap-4', className)}>
      <div className="flex flex-wrap items-center gap-4">
        {showPicker && (
          <div className="flex items-center gap-2">
            {/*
             * O rótulo que se vê é o da referência; o que o leitor de tela ouve
             * continua sendo o do `Select`, que diz O QUE é paginado — duas
             * listas na mesma tela teriam dois "Resultados por página".
             */}
            <span aria-hidden className="text-xs whitespace-nowrap text-texto-suave">
              Resultados por página:
            </span>
            <Select
              label={`Quantidade de ${label} por página`}
              hiddenLabel
              /*
               * `w-auto` derruba o `w-full` do `Select`, que é feito para
               * formulário: aqui ele divide a linha com a frase e os botões. O
               * `cn` do componente é tailwind-merge, então a última classe vence.
               */
              className="w-auto"
              value={perPage}
              onChange={(e) => onPerPageChange(Number(e.target.value))}
            >
              {options.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </Select>
          </div>
        )}

        {showButtons && (
          <p className="text-xs whitespace-nowrap tabular-nums text-texto-suave">
            {pageNumber(from)} a {pageNumber(until)} de {pageNumber(total)}
          </p>
        )}
      </div>

      {showButtons && (
        <nav aria-label={`Páginas de ${label}`} className="flex items-center gap-2">
          <Button
            variant="contorno"
            icon="seta-esquerda"
            disabled={page <= 1}
            aria-label={`Página anterior de ${label}`}
            className={cn(SQUARE, NEUTRAL)}
            onClick={() => onChange(page - 1)}
          />
          {pageItems(page, pages).map((item, i) =>
            item === '…' ? (
              <span
                key={`gap-${i}`}
                aria-hidden
                className="w-6 text-center text-xs text-texto-suave"
              >
                …
              </span>
            ) : (
              <Button
                key={item}
                variant="contorno"
                aria-label={`Página ${item} de ${label}`}
                aria-current={item === page ? 'page' : undefined}
                className={cn(NUMBER, 'tabular-nums', item === page ? CURRENT : NEUTRAL)}
                onClick={() => item !== page && onChange(item)}
              >
                {pageNumber(item)}
              </Button>
            ),
          )}
          <Button
            variant="contorno"
            icon="seta-direita"
            disabled={page >= pages}
            aria-label={`Próxima página de ${label}`}
            className={cn(SQUARE, NEUTRAL)}
            onClick={() => onChange(page + 1)}
          />
        </nav>
      )}
    </div>
  );
}

/*
 * O contorno NEUTRO dos botões que não são a página atual: borda de controle e
 * texto da casa. O laranja fica só com a página em que se está — é ele que diz
 * "você está aqui", e ele pararia de dizer se todo botão fosse laranja.
 */
const NEUTRAL =
  'border-borda-forte font-medium text-texto hover:bg-superficie-suave active:bg-superficie-suave';
